import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import notify from 'devextreme/ui/notify';
import { Button, ColorBox, DataGrid, SelectBox, Switch, TextBox, Popup, NumberBox, CheckBox } from 'devextreme-react';
import { Slider, Tooltip } from 'devextreme-react/slider';
import { RowDragging, Selection } from 'devextreme-react/cjs/data-grid';
import { ColorBoxTypes } from 'devextreme-react/cjs/color-box';
import { getUserName } from '../../../../api/sessions';
import { ThemeSettingsModel } from '../themeList';
import { fontOptions } from './fonts';

interface ThemeSettingsTabProps {
  selectedTheme?: any;
  isEditable: boolean;
  gridSettings: ThemeSettingsModel;
  setGridSettings: (settings: ThemeSettingsModel) => void;
}

export const ThemeSettingsTab = ({
  selectedTheme,
  isEditable,
  gridSettings,
  setGridSettings,
}: ThemeSettingsTabProps) => {
  const { t } = useTranslation();

  // Popup için ek state’ler (tema adı, açıklama, varsayılan)
  const [popupSettingName, setPopupSettingName] = useState('');
  const [popupComment, setPopupComment] = useState('');
  const [popupIsDefault, setPopupIsDefault] = useState(false);



  // Popup açılırken, selectedTheme varsa JSON'dan gelen ayarlar (ve popup alanları) yükleniyor.
  useEffect(() => {
    if (selectedTheme && selectedTheme.settingJson) {
      try {
        const parsed = JSON.parse(selectedTheme.settingJson);
        const userName = getUserName();
        // Eğer kullanıcıya ait ayarlar varsa; yoksa doğrudan parsed objesi:
        const userSettings = parsed[userName] || parsed;
        // Eğer nested "demo" varsa, bunu flat hale getirmek isterseniz (opsiyonel):
        const flatSettings =
          userSettings.demo
            ? { ...userSettings, ...userSettings.demo }
            : userSettings;
        // Parent'dan gelen gridSettings zaten güncellenmiş olmalı.
        setGridSettings(flatSettings);
        // Popup alanları için:
        setPopupSettingName(selectedTheme.settingName || "");
        setPopupComment(selectedTheme.comment || "");
        setPopupIsDefault(selectedTheme.isDefault === "1");
      } catch (e) {
        console.error("Tema ayarları parse edilemedi:", e);
      }
    }
  }, [selectedTheme, setGridSettings]);

  // updateFieldTheme: gridSettings'i güncelliyoruz.
  const updateFieldTheme = (key: keyof ThemeSettingsModel) => (value: any) => {
    setGridSettings({
      ...gridSettings,
      [key]: value,
    });
  };



  const handleColorChangeHeader = useCallback(
    ({ value }: ColorBoxTypes.ValueChangedEvent) => {
      updateFieldTheme("grid_header_color")(value);
    },
    [gridSettings]
  );

  const handleColorChangeRow = useCallback(
    ({ value }: ColorBoxTypes.ValueChangedEvent) => {
      updateFieldTheme("grid_row_color")(value);
    },
    [gridSettings]
  );

  return (
    <div>
      <div className='flex flex-col w-full gap-4'>


        <div className="p-2 mt-2 rounded-md flex flex-col md:flex-row justify-between border">
          <div className='flex flex-col gap-4 w-full md:w-72'>
            <div className="flex flex-col w-full">
              <div className="ml-2">Title Font</div>
              <div className="w-full">
                <div className="flex items-center">
                  <div className="flex-1">
                    <Slider
                      readOnly={!isEditable}
                      min={5}
                      max={50}
                      value={gridSettings?.grid_header_font}
                      onValueChange={updateFieldTheme('grid_header_font')}
                      className="!p-0 !m-0"

                    >
                      <Tooltip enabled={true} showMode="onHover" position="bottom" />
                    </Slider>
                  </div>
                  <span className="ml-2 relative whitespace-nowrap">
                    {gridSettings?.grid_header_font}px
                  </span>

                </div>
              </div>
            </div>
            <div className="flex flex-col w-full">
              <div className="ml-2">Title Padding</div>
              <div className="w-full">
                <div className="flex items-center">
                  <div className="flex-1">
                    <Slider
                      readOnly={!isEditable}
                      min={0}
                      max={50}
                      value={gridSettings?.grid_header_padding}
                      onValueChange={updateFieldTheme('grid_header_padding')}
                      className="!p-0 !m-0"

                    >
                      <Tooltip enabled={true} showMode="onHover" position="bottom" />
                    </Slider>
                  </div>
                  <span className="ml-2 relative whitespace-nowrap">
                    {gridSettings?.grid_header_padding}px
                  </span>

                </div>
              </div>
            </div>
            <div className="flex flex-col justify-between">
              <div className="mx-2">Title Font Family</div>
              <SelectBox
                dataSource={fontOptions}
                displayExpr="label"
                valueExpr="value"
                value={gridSettings?.grid_header_fontfamily}
                onValueChange={updateFieldTheme("grid_header_fontfamily")}
                readOnly={!isEditable}
                itemRender={(item) => (
                  <div style={{ fontFamily: item.value }}>
                    {item.label}
                  </div>
                )}
              />
            </div>
            <div className="flex w-full justify-between px-2">
              <div>Title Bold</div>
              <Switch
                value={gridSettings?.grid_header_bold}
                onValueChange={updateFieldTheme("grid_header_bold")}
                readOnly={!isEditable}
              />
            </div>
          </div>

          <div className='flex flex-col gap-4 w-full md:w-72'>
            <div className="flex flex-col w-full">
              <div className="ml-2">Row Font</div>
              <div className="w-full">
                <div className="flex items-center">
                  <div className="flex-1">
                    <Slider
                      readOnly={!isEditable}
                      min={5}
                      max={50}
                      value={gridSettings?.grid_row_font}
                      onValueChange={updateFieldTheme('grid_row_font')}
                      className="!p-0 !m-0"

                    >
                      <Tooltip enabled={true} showMode="onHover" position="bottom" />
                    </Slider>
                  </div>
                  <span className="ml-2 relative whitespace-nowrap">
                    {gridSettings?.grid_row_font}px
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col w-full">
              <div className="ml-2">Row Padding</div>
              <div className="w-full">
                <div className="flex items-center">
                  <div className="flex-1">
                    <Slider
                      readOnly={!isEditable}
                      min={0}
                      max={50}
                      value={gridSettings?.grid_row_padding}
                      onValueChange={updateFieldTheme('grid_row_padding')}
                      className="!p-0 !m-0"
                    >
                      <Tooltip enabled={true} showMode="onHover" position="bottom" />
                    </Slider>
                  </div>
                  <span className="ml-2 relative whitespace-nowrap">
                    {gridSettings?.grid_row_padding}px
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-between">
              <div className="mx-2">Row Font Family</div>
              <SelectBox
                dataSource={fontOptions}
                displayExpr="label"
                valueExpr="value"
                value={gridSettings?.grid_row_fontfamily}
                onValueChange={updateFieldTheme("grid_row_fontfamily")}
                readOnly={!isEditable}
                itemRender={(item) => (
                  <div style={{ fontFamily: item.value }}>
                    {item.label}
                  </div>
                )}
              />
            </div>
            <div className="flex w-full justify-between px-2">
              <div>Row Bold</div>
              <Switch
                value={gridSettings?.grid_row_bold}
                onValueChange={updateFieldTheme("grid_row_bold")}
                readOnly={!isEditable}
              />
            </div>
            <div className="flex flex-col justify-between">
              <div className="mx-2">Row Alterne Color</div>
              <div className='flex justify-between gap-4'>
                <ColorBox
                  value={gridSettings?.grid_row_alterneColor != "null" ? gridSettings?.grid_row_alterneColor : "#f5f5f5"}
                  onValueChange={updateFieldTheme("grid_row_alterneColor")}
                  readOnly={!isEditable}
                  applyValueMode="instantly"
                  disabled={gridSettings?.grid_row_alterneColor != "null" ? false : true}
                />

                <CheckBox
                  text={t("Default")}
                  value={gridSettings?.grid_row_alterneColor != "null" ? false : true}
                  onValueChange={(e) => {
                    if (e) {
                      updateFieldTheme("grid_row_alterneColor")("null");
                    } else {
                      updateFieldTheme("grid_row_alterneColor")("#f5f5f5");
                    }
                  }}
                  readOnly={!isEditable}
                />
              </div>

            </div>
          </div>


        </div>



        <div className="mt-2 rounded-md flex flex-col gap-4 border">
          <DataGrid
            className="custom-grid"
            showBorders
            rowAlternationEnabled
            dataSource={[
              { name: "test 1", field: "12345" },
              { name: "test 2", field: "54321" },
              { name: "test 3", field: "98765" },
            ]}
          >
            <Selection selectAllMode="allPages" showCheckBoxesMode="always" mode="multiple" />
            <RowDragging allowReordering={true} dropFeedbackMode="push" />
          </DataGrid>
        </div>
      </div>
      {/* Inline stil: Preview grid styling applied from gridSettings */}
      <style>
        {`
          .custom-grid .dx-datagrid-rowsview .dx-row td {
            padding: ${gridSettings?.grid_row_padding}px !important;
            font-size: ${gridSettings?.grid_row_font}px !important;
            font-weight: ${gridSettings?.grid_row_bold ? "bold" : "normal"} !important;
            color: ${gridSettings?.grid_row_color} !important;
            font-family: ${gridSettings?.grid_row_fontfamily} !important;
          }
          .custom-grid .dx-datagrid-headers .dx-datagrid-text-content {
            padding: ${gridSettings?.grid_header_padding}px !important;
            font-size: ${gridSettings?.grid_header_font}px !important;
            font-weight: ${gridSettings?.grid_header_bold ? "bold" : "normal"} !important;
            color: ${gridSettings?.grid_header_color} !important;
            font-family: ${gridSettings?.grid_header_fontfamily} !important;
          }
            .dx-row-alt {
              & > td {
                background-color: ${gridSettings.grid_row_alterneColor ?? "null"} !important;
              }
            }


        `}
      </style>
    </div>
  );
};
